import sanitizeHtml from "sanitize-html";
import { getCloudUrl } from "Constants";
import _ from "lodash";

const getAllowedTagsForHTMLTitle = () => {
  const tagsToRemove = [
    "col",
    "colgroup",
    "table",
    "tbody",
    "td",
    "tfoot",
    "th",
    "thead",
    "tr",
  ];

  return _.concat(
    _.difference(sanitizeHtml.defaults.allowedTags, tagsToRemove),
    ["img", "video"]
  );
};

const getAllowedSVGConfig = () => {
  return {
    svg: ["width", "height", "viewBox", "xmlns", "viewbox"],
    path: ["d", "fill", "stroke", "stroke-width", "fill-rule", "clip-rule"],
    circle: ["cx", "cy", "r", "fill", "stroke", "stroke-width"],
    rect: ["x", "y", "width", "height", "fill", "stroke", "stroke-width"],
    line: ["x1", "y1", "x2", "y2", "stroke", "stroke-width"],
    polyline: ["points", "fill", "stroke", "stroke-width"],
    polygon: ["points", "fill", "stroke", "stroke-width"],
    text: ["x", "y", "fill", "font-size", "font-family"],
    g: ["transform"],
  };
};

const getAllowedInputConfig = () => {
  return {
    input: [
      "type",
      "name",
      "value",
      "placeholder",
      "disabled",
      "readonly",
      "maxlength",
      "required",
      "autocomplete",
      "autocapitalize",
      "spellcheck",
    ],
  };
};

/**
 * Creates a standard transformation for media elements in HTML sanitization
 *
 * @param {Object} options - Configuration options
 * @param {string} [options.tag="img"] - The tag name to transform to
 * @param {string} [options.iconPath="/assets/webapp/ImageSquareOutlined.svg"] - Path to the icon (without base URL)
 * @param {string} [options.style=""] - Additional inline styles to apply
 * @returns {Function} The transformation function for sanitizeHtml
 */
const createMediaTransform = ({
  tag = "img",
  iconPath = "/assets/webapp/ImageSquareOutlined.svg",
  style = "",
} = {}) => {
  return sanitizeHtml.simpleTransform(tag, {
    height: "16px",
    width: "16px",
    src: `${getCloudUrl()}${iconPath}`,
    style,
  });
};

export const getStyleStrippedText = ({ text, type }) => {
  let config = {};

  switch (type) {
    case "RICH_TEXT_EDITOR":
      config = {
        allowedTags: [
          "a",
          "ol",
          "ul",
          "li",
          "em",
          "div",
          "span",
          "strong",
          "p",
          "img",
          "b",
        ],
        selfClosing: ["br", "img"],
        allowedAttributes: {
          a: ["rel", "href", "target"],
          div: ["style"],
          span: ["style"],
          img: [
            "data-*",
            "src",
            "style",
            "alt",
            "class",
            "height",
            "width",
            "vertical-align",
            "display",
          ],
        },
        allowedStyles: {
          div: {
            "text-align": [/^justify$/],
          },
          span: {
            "font-size": [/^\d+\.?\d*(?:px|rem|em|%)$/],
            "text-decoration": [/^underline$/],
          },
          img: {
            "vertical-align": [/.*/],
            height: [/.*/],
            width: [/.*/],
          },
        },
        // allowedSchemes: ["data"], // add "http" / "https" if we want to paste external image
        // filtering out the img tags which don't have src or an empty src
        exclusiveFilter: frame => {
          return frame.tag === "img" && !("src" in frame.attribs);
        },
      };
      break;
    case "DOCUMENT_EDITOR":
      config = {
        allowedTags: [
          "a",
          "ol",
          "ul",
          "li",
          "em",
          "div",
          "span",
          "strong",
          "p",
          "img",
          "b",
          "i",
          "br",
          "h3",
          "h4",
          "h5",
          "table",
          "colgroup",
          "col",
          "thead",
          "tbody",
          "tr",
          "th",
          "td",
          "iframe",
          "video",
          "pre",
          "code",
          "hr",
          "blockquote",
        ],
        selfClosing: ["br", "img", "hr"],
        allowedAttributes: {
          p: ["style", "class", "dir", "data-sessionid"],
          ol: ["style", "class", "dir", "data-sessionid"],
          ul: ["style", "class", "dir", "data-sessionid"],
          li: ["value", "class", "dir", "style", "role", "aria-checked"],
          h3: ["class", "dir", "style"],
          h4: ["class", "dir", "style"],
          h5: ["class", "dir", "style"],
          a: ["rel", "href", "target", "title", "class"],
          div: ["style", "class", "draggable", "data-*"],
          span: ["style", "class"],
          img: [
            "data-*",
            "src",
            "style",
            "alt",
            "class",
            "height",
            "width",
            "vertical-align",
            "display",
            "data-latex",
            "loading",
            "decoding",
          ],
          i: ["class"],
          strong: ["class", "style"],
          em: ["class", "style"],
          table: ["style", "class"],
          colgroup: ["style", "class"],
          col: ["style", "class"],
          td: ["style", "class"],
          tr: ["style", "class"],
          th: ["style", "class"],
          iframe: [
            "data-*",
            "src",
            "style",
            "class",
            "width",
            "height",
            "frameborder",
            "allow",
            "allowfullscreen",
            "loading",
          ],
          video: [
            "src",
            "controls",
            "autoplay",
            "muted",
            "loop",
            "playsinline",
            "width",
            "height",
            "poster",
            "preload",
            "class",
            "style",
            "data-*",
            "loading",
          ],
          code: ["class", "style", "data-*"],
          pre: ["style", "class", "data-*", "dir"],
          hr: ["style", "class", "data-*"],
          blockquote: ["style", "class", "dir"],
        },
        allowedStyles: {
          p: {
            "text-align": [/^.*$/],
            "padding-inline-start": [/^.*$/],
            "text-indent": [/^.*$/],
          },
          ul: {
            "padding-inline-start": [/^.*$/],
            "list-style-type": [/^.*$/],
          },
          ol: {
            "padding-inline-start": [/^.*$/],
          },
          li: {
            "padding-inline-start": [/^.*$/],
            "text-align": [/^.*$/],
            "text-indent": [/^.*$/],
          },
          div: {
            "text-align": [/^.*$/],
            //allow opacity, transform
            opacity: [/^.*$/],
            transform: [/^.*$/],
          },
          span: {
            "font-size": [/^(14px|16px|18px)$/],
            "text-decoration": [/^underline$/],
            "white-space": [/^pre-wrap$/],
            color: [
              /^#[0-9a-fA-F]{3,6}$/,
              /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/,
            ],
            background: [
              /^#[0-9a-fA-F]{3,6}$/,
              /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/,
            ],
          },
          img: {
            "vertical-align": [/^.*$/],
            height: [/^.*$/],
            width: [/^.*$/],
            display: [/^(inline|block)$/],
          },
          strong: {
            "font-size": [/^(14px|16px|18px)$/],
            color: [
              /^#[0-9a-fA-F]{3,6}$/,
              /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/,
            ],
            background: [
              /^#[0-9a-fA-F]{3,6}$/,
              /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/,
            ],
          },
          em: {
            "font-size": [/^(14px|16px|18px)$/],
            color: [
              /^#[0-9a-fA-F]{3,6}$/,
              /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/,
            ],
            background: [
              /^#[0-9a-fA-F]{3,6}$/,
              /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/,
            ],
          },
        },
        transformTags: {
          a: sanitizeHtml.simpleTransform("a", { target: "_blank" }),
          pre: (tagName, attribs) => {
            return {
              tagName,
              attribs: {
                ...attribs,
                class: attribs.class ? `${attribs.class} tde-code` : "tde-code",
              },
            };
          },
          video: (tagName, attribs) => ({
            tagName,
            attribs: {
              ...attribs,
              loading: "lazy",
            },
          }),
          img: (tagName, attribs) => ({
            tagName,
            attribs: {
              ...attribs,
              loading: "lazy",
              decoding: "async",
            },
          }),
          iframe: (tagName, attribs) => ({
            tagName,
            attribs: {
              ...attribs,
              loading: "lazy",
            },
          }),
        },
      };
      break;
    case "MEDIA_RESTRICTED_RICH_TEXT":
      config = {
        allowedTags: [
          "a",
          "ol",
          "ul",
          "li",
          "em",
          "div",
          "span",
          "strong",
          "p",
          "b",
        ],
        selfClosing: ["br"],
        allowedAttributes: {
          a: ["rel", "href", "target"],
          div: ["style"],
          span: ["style"],
        },
        allowedStyles: {
          div: {
            "text-align": [/^justify$/],
          },
          span: {
            "font-size": [/^\d+\.?\d*(?:px|rem|em|%)$/],
            "text-decoration": [/^underline$/],
          },
        },
      };
      break;
    case "SNS_EDITOR":
      config = {
        allowedTags: ["img"],
        selfClosing: ["img"],
        allowedAttributes: {
          img: [
            "data-*",
            "src",
            "style",
            "alt",
            "class",
            "height",
            "width",
            "vertical-align",
            "display",
          ],
        },
        allowedStyles: {
          img: {
            "vertical-align": [/.*/],
            height: [/.*/],
            width: [/.*/],
          },
        },
        exclusiveFilter: frame => {
          return (
            frame.tag === "img" &&
            (!("src" in frame.attribs) ||
              frame.attribs.src === "" ||
              !("data-latex" in frame.attribs))
          );
        },
      };
      break;
    case "NOTES_TITLE":
      config = {
        allowedTags: ["div", "span"],
        allowedStyles: {
          div: {
            display: [/.*/],
          },
        },
      };
      break;
    case "CONTENT_EDITABLE":
      config = {
        allowedTags: ["ol", "ul", "li", "span", "p", "div", "br"],
        selfClosing: ["br"],
        allowedAttributes: {
          "*": ["style"],
        },
        allowedStyles: {
          "*": {
            "text-decoration": [/^underline$/],
            "font-weight": [
              /[1-9]00/,
              /^normal$/,
              /^lighter$/,
              /^bold$/,
              /^bolder$/,
            ],
          },
        },
      };
      break;
    case "SANITIZE_DANGER_HTML_TITLE":
      config = {
        allowedTags: getAllowedTagsForHTMLTitle(),
        allowedAttributes: {
          "*": ["id", "class", "title", "style", "dir", "draggable", "data-*"],
          a: ["rel", "href", "name", "target"],
          li: ["value"],
          img: ["src", "srcset", "alt", "width", "height", "loading"],
          video: ["src", "width", "height"],
        },
        transformTags: {
          a: sanitizeHtml.simpleTransform("a", { target: "_blank" }),
          img: createMediaTransform(),
          video: createMediaTransform({ style: "margin: 0" }),
        },
        parseStyleAttributes: false,
      };
      break;
    case "SANITIZE_DANGER_HTML_PARAGRAPH":
      config = {
        allowedTags: sanitizeHtml.defaults.allowedTags.concat([
          "img",
          "iframe",
          ...Object.keys(getAllowedSVGConfig()),
          ...Object.keys(getAllowedInputConfig()),
        ]),
        allowedAttributes: {
          "*": ["id", "class", "title", "style", "dir", "draggable", "data-*"],
          a: ["rel", "href", "name", "target"],
          li: ["value"],
          img: ["src", "srcset", "alt", "width", "height", "loading"],
          iframe: [
            "src",
            "width",
            "height",
            "frameborder",
            "allow",
            "allowfullscreen",
          ],
          ...getAllowedSVGConfig(),
          ...getAllowedInputConfig(),
        },
        transformTags: {
          a: sanitizeHtml.simpleTransform("a", { target: "_blank" }),
        },
        parseStyleAttributes: false,
      };
      break;
    default:
      config = {
        allowedTags: ["em", "b", "div", "span", "p", "strong", "br"],
        allowedAttributes: {
          "*": ["style"],
        },
        allowedStyles: {
          "*": {
            "white-space": ["pre-wrap"],
            "font-weight": [
              /[1-9]00/,
              /^normal$/,
              /^lighter$/,
              /^bold$/,
              /^bolder$/,
            ],
          },
        },
      };
  }
  return sanitizeHtml(text, config);
};
